import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaDownload,
  FaFileCsv,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import api from "../../services/api";
import ConstructCard from "./analytics/ConstructCard";
import DeltaChart from "./analytics/DeltaChart";
import BoxPlotChart from "./analytics/BoxPlotChart";
import DatasetTable from "./analytics/DatasetTable";
import ConditionRadarChart from "./analytics/ConditionRadarChart";

const MANIP_CHECK_IDS = ["emotional_valence", "mc_emotion", "mc_attr", "mc_ingroup", "mc_outgroup"];

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];

const BEHAVIORAL_METRICS = [
  { key: "messagesPosted", label: "Messages Posted", description: "Original posts by participant" },
  { key: "commentsPosted", label: "Comments Posted", description: "Replies to posts" },
  { key: "likesGiven", label: "Likes Given", description: "Like interactions" },
  {
    key: "timeOnPlatform",
    label: "Time on Platform",
    description: "Session duration",
    format: (v) => {
      const m = Math.round(v / 60000);
      return m > 0 ? `${m}m` : `${Math.round(v / 1000)}s`;
    },
  },
];

const StudyAnalytics = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [includeTest, setIncludeTest] = useState(true);

  const adminToken = localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    if (!adminToken || !studyId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/studies/${studyId}/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { includeTest },
      });
      setData(response.data);
    } catch (err) {
      console.error("Error fetching study analytics:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, studyId, includeTest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await api.get(`/admin/studies/${studyId}/export`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { includeTest },
      });

      if (format === "csv") {
        const csv = exportToCSV(response.data);
        downloadBlob(csv, `study-${studyId}-${today()}.csv`, "text/csv");
      } else {
        const json = JSON.stringify(response.data, null, 2);
        downloadBlob(json, `study-${studyId}-${today()}.json`, "application/json");
      }
    } catch (err) {
      console.error("Export failed:", err);
      setError("Failed to export data");
    } finally {
      setExporting(null);
    }
  };

  const handleResearchExport = async (format) => {
    setExporting(`research-${format}`);
    try {
      const response = await api.get(`/admin/studies/${studyId}/research-export`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { format, includeTest, includeAI: "false", allThreads: "false" },
        ...(format === "csv" ? { responseType: "text" } : {}),
      });

      if (format === "csv") {
        downloadBlob(response.data, `research-events-${studyId}-${today()}.csv`, "text/csv");
      } else {
        const json = JSON.stringify(response.data, null, 2);
        downloadBlob(json, `research-events-${studyId}-${today()}.json`, "application/json");
      }
    } catch (err) {
      console.error("Research export failed:", err);
      setError("Failed to export research data");
    } finally {
      setExporting(null);
    }
  };

  const toggleSection = (key) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white mb-4"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
          <div className="bg-red-900/50 text-red-200 p-4 rounded-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  const conditions = data?.study?.conditions || [];
  const funnel = data?.funnel;
  const constructs = data?.constructs || [];

  // Group constructs by section
  const preConstructs = constructs.filter((c) => c.phase === "pre");
  const postConstructs = constructs.filter((c) => c.phase === "post");
  const manipChecks = postConstructs.filter((c) =>
    c.questions.some((q) => MANIP_CHECK_IDS.includes(q.questionId))
  );
  const regularPostConstructs = postConstructs.filter(
    (c) => !c.questions.some((q) => MANIP_CHECK_IDS.includes(q.questionId))
  );

  // Find pre/post pairs for delta charts
  const prePosts = findPrePostPairs(constructs);

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="flex items-center text-gray-400 hover:text-white mr-4"
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">{data?.study?.name}</h1>
              <p className="text-gray-400 text-sm">Study Analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Test data toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(e) => setIncludeTest(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
              />
              <span className="text-gray-400">Include test data</span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleExport("json")}
                disabled={exporting === "json"}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50"
              >
                <FaDownload /> Dataset
              </button>
              <button
                onClick={() => handleExport("csv")}
                disabled={exporting === "csv"}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50"
              >
                <FaFileCsv /> Dataset CSV
              </button>
              <button
                onClick={() => handleResearchExport("json")}
                disabled={exporting === "research-json"}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm disabled:opacity-50"
              >
                <FaDownload /> Events+Threads
              </button>
              <button
                onClick={() => handleResearchExport("csv")}
                disabled={exporting === "research-csv"}
                className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm disabled:opacity-50"
              >
                <FaFileCsv /> Events CSV
              </button>
            </div>
          </div>
        </div>

        {/* Panel 1: Dataset Overview */}
        <div className="bg-gray-800 rounded-lg p-5 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Dataset Overview</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Funnel */}
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Completion Funnel</h3>
              <FunnelTable funnel={funnel} conditions={conditions} />
            </div>

            {/* Attention Checks */}
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Attention Checks</h3>
              <AttentionChecksSummary checks={data?.attentionChecks} />
            </div>

            {/* Condition Balance */}
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Condition Balance</h3>
              <ConditionBalance funnel={funnel} conditions={conditions} />
            </div>
          </div>

          {/* Timing */}
          {(data?.timing?.preSurvey?.n > 0 || data?.timing?.postSurvey?.n > 0) && (
            <div className="mt-6 pt-4 border-t border-gray-700">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Phase Timing</h3>
              <TimingTable timing={data?.timing} conditions={conditions} />
            </div>
          )}
        </div>

        {/* Panel 2: Survey Descriptives */}
        {preConstructs.length > 0 && (
          <SectionHeader
            title="Pre-Survey Constructs"
            sectionKey="pre"
            collapsed={collapsedSections.pre}
            onToggle={() => toggleSection("pre")}
          >
            <div className="space-y-4">
              {preConstructs.map((c) => (
                <ConstructCard key={c.pageId} construct={c} conditions={conditions} />
              ))}
            </div>
          </SectionHeader>
        )}

        {regularPostConstructs.length > 0 && (
          <SectionHeader
            title="Post-Survey Constructs"
            sectionKey="post"
            collapsed={collapsedSections.post}
            onToggle={() => toggleSection("post")}
          >
            <div className="space-y-4">
              {regularPostConstructs.map((c) => (
                <ConstructCard key={c.pageId} construct={c} conditions={conditions} />
              ))}
            </div>
          </SectionHeader>
        )}

        {manipChecks.length > 0 && (
          <SectionHeader
            title="Manipulation Checks"
            sectionKey="manip"
            collapsed={collapsedSections.manip}
            onToggle={() => toggleSection("manip")}
            icon={<FaExclamationTriangle className="text-yellow-400 mr-2" />}
            subtitle="Verify experimental conditions"
          >
            <div className="space-y-4">
              {manipChecks.map((c) => (
                <ConstructCard
                  key={c.pageId}
                  construct={c}
                  conditions={conditions}
                  isManipCheck={true}
                />
              ))}
            </div>
          </SectionHeader>
        )}

        {/* Condition Profiles Radar */}
        {postConstructs.length > 0 && (
          <SectionHeader
            title="Condition Profiles"
            sectionKey="radar"
            collapsed={collapsedSections.radar}
            onToggle={() => toggleSection("radar")}
            subtitle="Cross-construct comparison — each axis is a construct, each polygon is a condition (normalized to 0-100%)"
          >
            <div className="bg-gray-800 rounded-lg p-4">
              <ConditionRadarChart
                constructs={constructs}
                conditions={conditions}
                height={450}
              />
            </div>
          </SectionHeader>
        )}

        {/* Pre/Post Delta Charts */}
        {prePosts.length > 0 && (
          <SectionHeader
            title="Pre/Post Comparisons"
            sectionKey="delta"
            collapsed={collapsedSections.delta}
            onToggle={() => toggleSection("delta")}
          >
            <div className="space-y-4">
              {prePosts.map((pair) => (
                <div key={pair.label} className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-medium text-sm mb-3">{pair.label}</h4>
                  <DeltaChart
                    preStats={pair.preStats}
                    postStats={pair.postStats}
                    conditions={conditions}
                    scaleInfo={pair.scaleInfo}
                    height={220}
                  />
                </div>
              ))}
            </div>
          </SectionHeader>
        )}

        {/* Panel 3: Interaction Behavior (box plots + summary) */}
        {data?.behavioral && Object.keys(data.behavioral.byCondition).length > 0 && (
          <SectionHeader
            title="Interaction Behavior"
            sectionKey="behavioral"
            collapsed={collapsedSections.behavioral}
            onToggle={() => toggleSection("behavioral")}
          >
            {data.behavioral.distributions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {BEHAVIORAL_METRICS.map((metric) => (
                  <div key={metric.key} className="bg-gray-800 rounded-lg p-4">
                    <h4 className="text-white font-medium text-sm mb-2">{metric.label}</h4>
                    <BoxPlotChart
                      distributions={Object.fromEntries(
                        conditions.map((c) => [
                          c.conditionId,
                          data.behavioral.distributions[c.conditionId]?.[metric.key],
                        ])
                      )}
                      conditions={conditions}
                      metricLabel={metric.description}
                      formatValue={metric.format}
                      height={200}
                    />
                  </div>
                ))}
              </div>
            )}
            <BehavioralSummary behavioral={data.behavioral} conditions={conditions} />

            {/* Interaction Type Breakdown */}
            {data.behavioral.interactionTypes && (
              <div className="mt-4">
                <InteractionTypeBreakdown
                  interactionTypes={data.behavioral.interactionTypes}
                  conditions={conditions}
                />
              </div>
            )}
          </SectionHeader>
        )}

        {/* Selective Exposure: Ideological Distance */}
        {data?.behavioral?.ideologicalDistance && (
          <SectionHeader
            title="Selective Exposure"
            sectionKey="selectiveExposure"
            collapsed={collapsedSections.selectiveExposure}
            onToggle={() => toggleSection("selectiveExposure")}
            subtitle="Ideological distance of participant interactions"
          >
            <IdeologicalDistancePanel
              ideologicalDistance={data.behavioral.ideologicalDistance}
              conditions={conditions}
            />
          </SectionHeader>
        )}

        {/* Feed Exposure Analysis */}
        {data?.behavioral?.feedExposure && (
          <SectionHeader
            title="Feed Exposure"
            sectionKey="feedExposure"
            collapsed={collapsedSections.feedExposure}
            onToggle={() => toggleSection("feedExposure")}
            subtitle="What content was served to participants"
          >
            <FeedExposurePanel
              feedExposure={data.behavioral.feedExposure}
              conditions={conditions}
            />
          </SectionHeader>
        )}

        {/* Dwell Time Analysis */}
        {data?.behavioral?.dwellTime && (
          <SectionHeader
            title="Message Dwell Time"
            sectionKey="dwellTime"
            collapsed={collapsedSections.dwellTime}
            onToggle={() => toggleSection("dwellTime")}
            subtitle="How long participants viewed messages"
          >
            <DwellTimePanel
              dwellTime={data.behavioral.dwellTime}
              conditions={conditions}
            />
          </SectionHeader>
        )}

        {/* Dataset Table */}
        <SectionHeader
          title="Dataset Table"
          sectionKey="dataset"
          collapsed={collapsedSections.dataset !== false}
          onToggle={() => toggleSection("dataset")}
          subtitle={`${funnel?.total?.assigned || 0} participants`}
        >
          <DatasetTable studyId={studyId} conditions={conditions} />
        </SectionHeader>
      </div>
    </div>
  );
};

// --- Sub-components ---

const SectionHeader = ({ title, collapsed, onToggle, children, icon, subtitle }) => (
  <div className="mb-6">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-3 px-1 hover:bg-gray-800/50 rounded transition-colors"
    >
      <div className="flex items-center">
        {icon}
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {subtitle && <span className="text-gray-400 text-sm ml-2">— {subtitle}</span>}
      </div>
      {collapsed ? (
        <FaChevronDown className="text-gray-400" />
      ) : (
        <FaChevronUp className="text-gray-400" />
      )}
    </button>
    {!collapsed && <div className="mt-3">{children}</div>}
  </div>
);

const FunnelTable = ({ funnel, conditions }) => {
  if (!funnel) return <p className="text-gray-500 text-sm">No data</p>;

  const steps = [
    { key: "assigned", label: "Assigned" },
    { key: "preDone", label: "Pre-survey" },
    { key: "instanceCreated", label: "Platform" },
    { key: "postDone", label: "Post-survey" },
    { key: "completed", label: "Completed" },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400 border-b border-gray-700">
            <th className="text-left pb-2 pr-2">Step</th>
            {conditions.map((c) => (
              <th key={c.conditionId} className="text-right pb-2 px-1">
                {c.label?.split(" ").slice(0, 2).join(" ") || c.conditionId}
              </th>
            ))}
            <th className="text-right pb-2 pl-2 font-bold">Total</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step) => (
            <tr key={step.key} className="border-b border-gray-700/30">
              <td className="py-1.5 pr-2 text-gray-300">{step.label}</td>
              {conditions.map((c) => (
                <td key={c.conditionId} className="text-right py-1.5 px-1 text-gray-400">
                  {funnel.byCondition[c.conditionId]?.[step.key] || 0}
                </td>
              ))}
              <td className="text-right py-1.5 pl-2 text-white font-medium">
                {funnel.total[step.key] || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AttentionChecksSummary = ({ checks }) => {
  if (!checks || Object.keys(checks).length === 0) {
    return <p className="text-gray-500 text-sm">No attention checks configured</p>;
  }

  return (
    <div className="space-y-2">
      {Object.entries(checks).map(([qId, check]) => (
        <div key={qId} className="flex items-center justify-between">
          <span className="text-gray-300 text-xs">{qId}</span>
          <div className="flex items-center gap-2">
            {check.rate >= 0.8 ? (
              <FaCheckCircle className="text-green-400 w-3 h-3" />
            ) : check.rate >= 0.6 ? (
              <FaExclamationTriangle className="text-yellow-400 w-3 h-3" />
            ) : (
              <FaTimesCircle className="text-red-400 w-3 h-3" />
            )}
            <span className="text-white text-xs font-medium">
              {(check.rate * 100).toFixed(0)}%
            </span>
            <span className="text-gray-500 text-xs">
              ({check.correct}/{check.total})
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const ConditionBalance = ({ funnel, conditions }) => {
  if (!funnel?.byCondition) return <p className="text-gray-500 text-sm">No data</p>;

  const maxAssigned = Math.max(
    ...conditions.map((c) => funnel.byCondition[c.conditionId]?.assigned || 0),
    1
  );

  return (
    <div className="space-y-2">
      {conditions.map((c, i) => {
        const count = funnel.byCondition[c.conditionId]?.assigned || 0;
        const pct = (count / maxAssigned) * 100;
        return (
          <div key={c.conditionId}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-300">{c.label || c.conditionId}</span>
              <span className="text-white font-medium">{count}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${pct}%`,
                  backgroundColor: CONDITION_COLORS[i % CONDITION_COLORS.length],
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TimingTable = ({ timing, conditions }) => {
  const formatDuration = (ms) => {
    if (!ms) return "—";
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const phases = [
    { key: "preSurvey", label: "Pre-survey" },
    { key: "postSurvey", label: "Post-survey" },
  ].filter((p) => timing?.[p.key]?.n > 0);

  if (phases.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400 border-b border-gray-700">
            <th className="text-left pb-2 pr-4">Phase</th>
            <th className="text-right pb-2 px-2">Overall</th>
            {conditions.map((c) => (
              <th key={c.conditionId} className="text-right pb-2 px-2">
                {c.label?.split(" ").slice(0, 2).join(" ") || c.conditionId}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {phases.map((phase) => {
            const phaseData = timing[phase.key];
            return (
              <tr key={phase.key} className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">{phase.label}</td>
                <td className="text-right py-1.5 px-2 text-white">
                  {formatDuration(phaseData.mean)}
                  <span className="text-gray-500 ml-1">
                    ±{formatDuration(phaseData.sd)}
                  </span>
                </td>
                {conditions.map((c) => {
                  const cd = phaseData.byCondition?.[c.conditionId];
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-gray-400">
                      {cd ? (
                        <>
                          {formatDuration(cd.mean)}
                          <span className="text-gray-600 ml-1">
                            ±{formatDuration(cd.sd)}
                          </span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const BehavioralSummary = ({ behavioral, conditions }) => {
  const metrics = [
    { key: "messagesPosted", label: "Messages posted" },
    { key: "commentsPosted", label: "Comments posted" },
    { key: "likesGiven", label: "Likes given" },
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left pb-2 pr-4">Metric</th>
              {conditions.map((c) => (
                <th key={c.conditionId} className="text-right pb-2 px-2">
                  {c.label || c.conditionId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => (
              <tr key={metric.key} className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">{metric.label}</td>
                {conditions.map((c) => (
                  <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                    {behavioral.byCondition[c.conditionId]?.[metric.key] || 0}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-gray-700/30">
              <td className="py-1.5 pr-4 text-gray-300">Avg time on platform</td>
              {conditions.map((c) => {
                const bucket = behavioral.byCondition[c.conditionId];
                const avgMs = bucket?.count > 0 ? bucket.timeOnPlatform / bucket.count : 0;
                const minutes = Math.round(avgMs / 60000);
                return (
                  <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                    {minutes > 0 ? `${minutes}m` : "—"}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const INTERACTION_TYPE_LABELS = {
  like: "Likes",
  unlike: "Unlikes",
  comment: "Comments",
  repost: "Reposts",
  follow: "Follows",
  unfollow: "Unfollows",
  expand_comments: "Thread expansions",
};

const InteractionTypeBreakdown = ({ interactionTypes, conditions }) => {
  // Collect all types across conditions
  const allTypes = new Set();
  Object.values(interactionTypes).forEach((types) =>
    Object.keys(types).forEach((t) => allTypes.add(t))
  );
  const typeList = [...allTypes].sort();

  if (typeList.length === 0) {
    return <p className="text-gray-500 text-sm">No interaction data</p>;
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h4 className="text-white font-medium text-sm mb-3">Interaction Type Breakdown</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left pb-2 pr-4">Type</th>
              {conditions.map((c) => (
                <th key={c.conditionId} className="text-right pb-2 px-2">
                  {c.label || c.conditionId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {typeList.map((type) => (
              <tr key={type} className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">
                  {INTERACTION_TYPE_LABELS[type] || type}
                </td>
                {conditions.map((c) => (
                  <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                    {interactionTypes[c.conditionId]?.[type] || 0}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const IdeologicalDistancePanel = ({ ideologicalDistance, conditions }) => {
  const hasData = conditions.some(
    (c) => ideologicalDistance.byCondition[c.conditionId]?.overall?.n > 0
  );

  if (!hasData) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <p className="text-gray-500 text-sm">No ideological distance data recorded</p>
      </div>
    );
  }

  // Collect all interaction types that have distance data
  const allTypes = new Set();
  Object.values(ideologicalDistance.byCondition).forEach((bucket) =>
    Object.keys(bucket.byType || {}).forEach((t) => allTypes.add(t))
  );
  const typeList = [...allTypes].sort();

  return (
    <div className="space-y-4">
      {/* Overall distance by condition */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-white font-medium text-sm mb-3">
          Mean Ideological Distance of Interactions
        </h4>
        <p className="text-gray-500 text-xs mb-3">
          Higher values = participants engaged with more ideologically distant content.
          Lower values = echo chamber behavior.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left pb-2 pr-4">Metric</th>
                {conditions.map((c) => (
                  <th key={c.conditionId} className="text-right pb-2 px-2">
                    {c.label || c.conditionId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-700/30 font-medium">
                <td className="py-1.5 pr-4 text-gray-300">Overall distance</td>
                {conditions.map((c) => {
                  const d = ideologicalDistance.byCondition[c.conditionId]?.overall;
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                      {d?.n > 0 ? (
                        <>
                          {d.mean.toFixed(3)}
                          <span className="text-gray-500 ml-1">±{d.sd.toFixed(3)}</span>
                          <span className="text-gray-600 ml-1">(n={d.n})</span>
                        </>
                      ) : "—"}
                    </td>
                  );
                })}
              </tr>
              {typeList.map((type) => (
                <tr key={type} className="border-b border-gray-700/30">
                  <td className="py-1.5 pr-4 text-gray-400 pl-3">
                    {INTERACTION_TYPE_LABELS[type] || type}
                  </td>
                  {conditions.map((c) => {
                    const d = ideologicalDistance.byCondition[c.conditionId]?.byType?.[type];
                    return (
                      <td key={c.conditionId} className="text-right py-1.5 px-2 text-gray-300">
                        {d?.n > 0 ? (
                          <>
                            {d.mean.toFixed(3)}
                            <span className="text-gray-600 ml-1">(n={d.n})</span>
                          </>
                        ) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const FeedExposurePanel = ({ feedExposure, conditions }) => {
  const hasData = conditions.some(
    (c) => feedExposure.byCondition[c.conditionId]?.totalImpressions > 0
  );

  if (!hasData) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <p className="text-gray-500 text-sm">No feed impression data recorded</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h4 className="text-white font-medium text-sm mb-3">Content Exposure by Condition</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left pb-2 pr-4">Metric</th>
              {conditions.map((c) => (
                <th key={c.conditionId} className="text-right pb-2 px-2">
                  {c.label || c.conditionId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { key: "totalImpressions", label: "Feed page loads" },
              { key: "totalMessagesShown", label: "Total messages served" },
              { key: "avgMessagesPerFeedLoad", label: "Avg messages/feed load", format: (v) => Number(v)?.toFixed(1) },
              { key: "avgMessagesPerParticipant", label: "Total served/participant", format: (v) => v?.toFixed(1) },
              { key: "proStance", label: "Pro-stance messages" },
              { key: "contraStance", label: "Contra-stance messages" },
              { key: "proContraRatio", label: "Pro/contra ratio" },
              { key: "artificial", label: "AI-authored messages" },
              { key: "human", label: "Human-authored messages" },
              { key: "artificialPct", label: "% AI content", format: (v) => `${v}%` },
            ].map((row) => (
              <tr key={row.key} className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">{row.label}</td>
                {conditions.map((c) => {
                  const val = feedExposure.byCondition[c.conditionId]?.[row.key];
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                      {val != null ? (row.format ? row.format(val) : val) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DwellTimePanel = ({ dwellTime, conditions }) => {
  const hasData = conditions.some(
    (c) => dwellTime.byCondition[c.conditionId]?.messageCount > 0
  );

  if (!hasData) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <p className="text-gray-500 text-sm">No dwell time data recorded</p>
      </div>
    );
  }

  const formatMs = (ms) => {
    if (!ms) return "—";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-white font-medium text-sm mb-3">
          Per-Message Dwell Time by Condition
        </h4>
        <p className="text-gray-500 text-xs mb-3">
          Average time participants spent viewing each message in their feed.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left pb-2 pr-4">Metric</th>
                {conditions.map((c) => (
                  <th key={c.conditionId} className="text-right pb-2 px-2">
                    {c.label || c.conditionId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">Avg dwell / message</td>
                {conditions.map((c) => {
                  const d = dwellTime.byCondition[c.conditionId];
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                      {d?.messageCount > 0 ? formatMs(d.avgDwellPerMessage) : "—"}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">Unique messages viewed</td>
                {conditions.map((c) => {
                  const d = dwellTime.byCondition[c.conditionId];
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                      {d?.uniqueMessageCount || 0}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">Viewport impressions</td>
                {conditions.map((c) => {
                  const d = dwellTime.byCondition[c.conditionId];
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-gray-400 text-xs">
                      {d?.messageCount || 0}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-b border-gray-700/30">
                <td className="py-1.5 pr-4 text-gray-300">Participants tracked</td>
                {conditions.map((c) => {
                  const d = dwellTime.byCondition[c.conditionId];
                  return (
                    <td key={c.conditionId} className="text-right py-1.5 px-2 text-white">
                      {d?.n || 0}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Box plots for dwell time distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="text-white font-medium text-sm mb-2">Avg Dwell per Message</h4>
          <BoxPlotChart
            distributions={Object.fromEntries(
              conditions.map((c) => [
                c.conditionId,
                dwellTime.byCondition[c.conditionId]?.distributions?.avgDwellPerMessage,
              ])
            )}
            conditions={conditions}
            formatValue={formatMs}
            height={200}
          />
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="text-white font-medium text-sm mb-2">Total Dwell per Participant</h4>
          <BoxPlotChart
            distributions={Object.fromEntries(
              conditions.map((c) => [
                c.conditionId,
                dwellTime.byCondition[c.conditionId]?.distributions?.totalDwell,
              ])
            )}
            conditions={conditions}
            formatValue={formatMs}
            height={200}
          />
        </div>
      </div>
    </div>
  );
};

// --- Helpers ---

function findPrePostPairs(constructs) {
  const pairs = [];

  // Match by question ID patterns: trust_pre_* <-> trust_post_*, global_stance_pre <-> global_stance_post
  const preConstructMap = {};
  const postConstructMap = {};

  for (const c of constructs) {
    for (const q of c.questions) {
      if (q.type === "single_choice") continue; // Skip categoricals
      const stats = q.aggregateStats || q.stats;
      if (!stats) continue;

      if (c.phase === "pre") {
        preConstructMap[q.questionId] = { stats, scaleInfo: q.scaleInfo, label: q.label, pageLabel: c.label };
      } else {
        postConstructMap[q.questionId] = { stats, scaleInfo: q.scaleInfo, label: q.label, pageLabel: c.label };
      }
    }
  }

  // Look for matching pre/post patterns
  for (const [preId, preData] of Object.entries(preConstructMap)) {
    // Try direct mappings: X_pre -> X_post, trust_pre_X -> trust_post_X
    const postId = preId
      .replace(/_pre_/, "_post_")
      .replace(/_pre$/, "_post");

    if (postId !== preId && postConstructMap[postId]) {
      pairs.push({
        label: `${preData.label || preId} (pre vs post)`,
        preStats: preData.stats,
        postStats: postConstructMap[postId].stats,
        scaleInfo: preData.scaleInfo || postConstructMap[postId].scaleInfo,
      });
    }
  }

  return pairs;
}

function today() {
  return new Date().toISOString().split("T")[0];
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function exportToCSV(exportData) {
  if (!exportData?.participants?.length) return "";

  const participants = exportData.participants;

  // Collect all response keys across all participants
  const preKeys = new Set();
  const postKeys = new Set();

  for (const p of participants) {
    if (p.preSurveyResponses) Object.keys(p.preSurveyResponses).forEach((k) => preKeys.add(k));
    if (p.postSurveyResponses) Object.keys(p.postSurveyResponses).forEach((k) => postKeys.add(k));
  }

  const sortedPreKeys = [...preKeys].sort();
  const sortedPostKeys = [...postKeys].sort();

  // Build headers
  const metaCols = ["participantId", "conditionId", "conditionLabel", "assignedAt", "completedAt"];
  const behavCols = ["behav_messagesPosted", "behav_commentsPosted", "behav_totalInteractions", "behav_timeOnPlatform"];
  const preCols = sortedPreKeys.map((k) => `pre_${k}`);
  const postCols = sortedPostKeys.map((k) => `post_${k}`);
  const timingCols = ["preSurveyDurationMs", "postSurveyDurationMs"];

  const headers = [...metaCols, ...timingCols, ...preCols, ...behavCols, ...postCols];

  const rows = participants.map((p) => {
    const row = {};
    // Metadata
    row.participantId = p.participantId;
    row.conditionId = p.conditionId;
    row.conditionLabel = p.conditionLabel;
    row.assignedAt = p.assignedAt || "";
    row.completedAt = p.completedAt || "";
    row.preSurveyDurationMs = p.preSurveyDurationMs || "";
    row.postSurveyDurationMs = p.postSurveyDurationMs || "";

    // Pre-survey
    for (const k of sortedPreKeys) {
      row[`pre_${k}`] = p.preSurveyResponses?.[k] ?? "";
    }

    // Behavioral
    row.behav_messagesPosted = p.behavioralData?.messagesPosted ?? "";
    row.behav_commentsPosted = p.behavioralData?.commentsPosted ?? "";
    row.behav_totalInteractions = p.behavioralData?.totalInteractions ?? "";
    row.behav_timeOnPlatform = p.behavioralData?.timeOnPlatform ?? "";

    // Post-survey
    for (const k of sortedPostKeys) {
      row[`post_${k}`] = p.postSurveyResponses?.[k] ?? "";
    }

    return headers.map((h) => {
      const val = row[h] ?? "";
      // Escape CSV values
      const str = String(val);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    });
  });

  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

export default StudyAnalytics;
